import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthService } from '../login/auth.service';
import { Organization } from 'src/typings';
import {
  CreateOrgBankAccountDto,
  CreateOrgDto,
  OrgBankAccount,
  SearchOrgDto,
  UpdateOrgBankAccountDto,
  UpdateOrgDto
} from './org.dto';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class OrgService {
  constructor(private http: HttpClient, private authService: AuthService) {}

  private normalizeIban(iban?: string) {
    return iban?.trim().toUpperCase();
  }

  private normalizeBankAccount(account: OrgBankAccount): OrgBankAccount {
    return {
      ...account,
      iban: this.normalizeIban(account.iban) || account.iban
    };
  }

  private normalizeOrganization(org: Organization): Organization {
    return {
      ...org,
      bankAccounts: (org.bankAccounts || []).map((account) => this.normalizeBankAccount(account))
    };
  }

  create(payload: CreateOrgDto) {
    return this.http
      .post<Organization>(`${environment.BACKEND}/org/create`, payload)
      .pipe(map((response) => this.normalizeOrganization(response)));
  }

  update(payload: UpdateOrgDto) {
    return this.http
      .put<Organization>(`${environment.BACKEND}/org/update`, payload)
      .pipe(map((response) => this.normalizeOrganization(response)));
  }

  getOne(id: number) {
    return this.http
      .get<Organization>(`${environment.BACKEND}/org/${id}`)
      .pipe(map((response) => this.normalizeOrganization(response)));
  }

  delete(id: number) {
    return this.http.delete<void>(`${environment.BACKEND}/org/${id}`);
  }

  search(payload?: SearchOrgDto) {
    return this.http
      .post<{ data: Organization[]; length: number }>(`${environment.BACKEND}/org/search`, payload)
      .pipe(
        map((response) => ({
          data: (response.data || []).map((org) => this.normalizeOrganization(org)),
          length: response.length
        }))
      );
  }

  getBankAccounts(orgId: number) {
    return this.http
      .get<OrgBankAccount[]>(`${environment.BACKEND}/org/${orgId}/bank-accounts`)
      .pipe(map((accounts) => (accounts || []).map((account) => this.normalizeBankAccount(account))));
  }

  createBankAccount(payload: CreateOrgBankAccountDto) {
    return this.http
      .post<OrgBankAccount>(`${environment.BACKEND}/org/bank-account/create`, {
        ...payload,
        iban: this.normalizeIban(payload.iban)
      })
      .pipe(map((response) => this.normalizeBankAccount(response)));
  }

  updateBankAccount(payload: UpdateOrgBankAccountDto) {
    return this.http
      .put<OrgBankAccount>(`${environment.BACKEND}/org/bank-account/update`, {
        ...payload,
        iban: payload.iban ? this.normalizeIban(payload.iban) : undefined
      })
      .pipe(map((response) => this.normalizeBankAccount(response)));
  }

  deleteBankAccount(id: number) {
    return this.http.delete<void>(`${environment.BACKEND}/org/bank-account/${id}`);
  }
}
